import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../services/api'
import ArtifactStatusBadge from '../components/artifacts/ArtifactStatusBadge'

// Phase 2: data shape from Api::V1::InitiativesController#show is
//   {
//     id, name, slug, description,
//     team: [{ user_initiative_id, role, user: { id, full_name, title, company: { id, name } } }],
//     recent_sessions: [],                       // legacy stub — always empty
//     recent_responses: [{ id, status, modality, received_at, prompt, user }],
//     recent_artifacts: [{ id, name, ..., user: { full_name, company } }],
//     latest_insights: [...]
//   }
//
// This page renames its old person/* shape to user/* and surfaces
// recent_responses (Phase 2) instead of recent_sessions (Phase 1 stub).

export default function InitiativeDetail() {
  const { slug } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .get(`/api/v1/initiatives/${slug}`)
      .then((payload) => {
        if (!cancelled) setData(payload)
      })
      .catch((err) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  if (loading) return <p className="text-sm text-slate-500">Loading initiative…</p>
  if (error) return <p className="text-sm text-rose-600">{error}</p>
  if (!data) return null

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      {/* Header */}
      <header>
        <Link to="/initiatives" className="text-xs font-medium text-slate-500 hover:text-slate-700">
          ← All initiatives
        </Link>
        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{data.name}</h2>
        {data.description && (
          <p className="mt-2 text-base text-slate-600 max-w-3xl leading-relaxed">
            {data.description}
          </p>
        )}
      </header>

      {/* Insights */}
      {data.latest_insights && data.latest_insights.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-accent-700 mb-3">
            AI insights
          </h3>
          <div className="rounded-xl border border-accent-100 bg-accent-50/50 p-6">
            <div className="text-sm text-slate-700 whitespace-pre-wrap">
              {data.latest_insights[0].content}
            </div>
            <div className="mt-3 text-xs text-slate-400">
              Generated {new Date(data.latest_insights[0].generated_at).toLocaleString()}
            </div>
          </div>
        </section>
      )}

      {/* Team */}
      {data.team && data.team.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
            Team ({data.team.length})
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.team.map((member) => (
              <div
                key={member.user_initiative_id}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {member.user?.full_name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {member.user?.title || '—'}
                    </div>
                  </div>
                  {member.user?.company?.name && (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                      {member.user.company.name}
                    </span>
                  )}
                </div>
                {member.role && (
                  <div className="mt-2 text-xs text-accent-700">{member.role}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent responses — paginated (Phase 2 replacement for recent_sessions) */}
      {data.response_count > 0 && (
        <ResponsesSection
          initiativeSlug={slug}
          initialResponses={data.recent_responses || []}
          totalCount={data.response_count}
        />
      )}

      {/* Recent artifacts */}
      {data.recent_artifacts && data.recent_artifacts.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
            Recent artifacts
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.recent_artifacts.map((artifact) => (
              <a
                key={artifact.id}
                href={artifact.file_url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-accent-300 transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {artifact.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {artifact.user?.full_name}
                      {artifact.user?.company && ` · ${artifact.user.company}`}
                    </div>
                  </div>
                  <ArtifactStatusBadge status={artifact.processing_status} />
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {(!data.team?.length &&
        !data.response_count &&
        !data.recent_artifacts?.length &&
        !data.latest_insights?.length) && (
        <p className="text-sm text-slate-500">
          Nothing here yet. Once team members respond to prompts or upload documents, they'll show
          up on this page.
        </p>
      )}
    </div>
  )
}

const PER_PAGE = 20

// Paginated "Recent responses" feed. Page 1 is seeded from the initiative
// #show payload (no extra fetch on load); paging hits
// GET /api/v1/initiatives/:id/responses.
function ResponsesSection({ initiativeSlug, initialResponses, totalCount }) {
  const [page, setPage] = useState(1)
  const [responses, setResponses] = useState(initialResponses)
  const [total, setTotal] = useState(totalCount)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return
    setLoading(true)
    setError(null)
    api
      .get(`/api/v1/initiatives/${initiativeSlug}/responses?page=${nextPage}&per_page=${PER_PAGE}`)
      .then((payload) => {
        setResponses(payload.responses || [])
        setTotal(payload.total ?? total)
        setPage(payload.page ?? nextPage)
      })
      .catch((err) => setError(err.message || 'Failed to load responses'))
      .finally(() => setLoading(false))
  }

  const rangeStart = (page - 1) * PER_PAGE + 1
  const rangeEnd = Math.min(page * PER_PAGE, total)

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
        Recent responses
        {total > responses.length || totalPages > 1 ? (
          <span className="ml-2 normal-case font-normal text-slate-400">
            showing {rangeStart}–{rangeEnd} of {total}
          </span>
        ) : null}
      </h3>

      {error && (
        <div className="mb-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>
      )}

      <div className={`space-y-2 ${loading ? 'opacity-50' : ''}`}>
        {responses.map((response) => (
          <div
            key={response.id}
            className="rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between gap-4"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">
                {response.user?.full_name}
                {response.user?.company && (
                  <span className="ml-2 text-xs text-slate-400">{response.user.company}</span>
                )}
              </div>
              {response.prompt?.question_text && (
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {response.prompt.question_text}
                </div>
              )}
            </div>
            <span className="shrink-0 inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
              {response.status}
            </span>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <nav className="flex items-center justify-between pt-3">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => goToPage(page - 1)}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => goToPage(page + 1)}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </nav>
      )}
    </section>
  )
}
